import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Plus, 
  Search, 
  MapPin, 
  Phone, 
  Mail,
  Edit,
  Trash2,
  Globe,
  Filter
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchGolfClubs, fetchCities, deleteClub } from '../services/clubsApi';
import CustomSelect from '../components/common/CustomSelect';

const AdminClubs = () => {
  const { isDarkMode } = useTheme();
  const [clubs, setClubs] = useState([]);
  const [filteredClubs, setFilteredClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [cities, setCities] = useState([]);

  useEffect(() => {
    loadClubs();
    loadCities();
  }, []);

  useEffect(() => {
    filterClubs();
  }, [clubs, searchTerm, selectedCity]);

  const loadClubs = async () => {
    try {
      setLoading(true);
      const data = await fetchGolfClubs({ 
        active: false,
        withCoordinates: false
      });
      setClubs(data);
    } catch (error) {
      console.error('Error loading clubs:', error);
      alert('Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const citiesData = await fetchCities();
      setCities(citiesData);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const filterClubs = () => {
    let filtered = [...clubs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(club => 
        club.name.toLowerCase().includes(term) ||
        (club.address && club.address.toLowerCase().includes(term)) ||
        (club.city && club.city.toLowerCase().includes(term))
      );
    }

    if (selectedCity !== 'all') {
      filtered = filtered.filter(club => club.city === selectedCity);
    }

    setFilteredClubs(filtered);
  };

  const handleDelete = async (clubId, clubName) => {
    if (!window.confirm(`Are you sure you want to delete ${clubName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteClub(clubId);
      alert('Club deleted successfully');
      loadClubs();
    } catch (error) {
      console.error('Error deleting club:', error);
      alert(error.message || 'Failed to delete club');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading clubs...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Golf Clubs
            </h1>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Manage affiliated golf clubs in Free State
            </p>
          </div>
          <Link
            to="/admin/clubs/add"
            className="btn btn-primary inline-flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Club
          </Link>
        </div>

        {/* Filters */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode 
            ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
            : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none ${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
              }`} />
              <input
                type="text"
                placeholder="Search clubs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>

            {/* City Filter */}
            <CustomSelect
              name="city"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              options={[
                { value: 'all', label: 'All Cities' },
                ...cities.map(city => ({ value: city, label: city }))
              ]}
              placeholder="All Cities"
            />
          </div>

          {/* Results Count */}
          <div className={`mt-4 pt-4 border-t ${
            isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
          }`}>
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Showing <span className="font-semibold">{filteredClubs.length}</span> of <span className="font-semibold">{clubs.length}</span> clubs
            </p>
          </div>
        </div>

        {/* Clubs Grid */}
        {filteredClubs.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <Building2 className={`w-20 h-20 mx-auto mb-4 ${
              isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${
              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
            }`}>
              No clubs found
            </h3>
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
            }`}>
              {searchTerm || selectedCity !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Get started by adding your first club'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredClubs.map(club => (
              <div
                key={club.id}
                className={`p-6 rounded-xl border transition-all hover:shadow-lg ${
                  isDarkMode 
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50' 
                    : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50 shadow-sm'
                }`}
              >
                {/* Club Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-xl font-bold mb-2 truncate ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      {club.name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      club.active
                        ? isDarkMode
                          ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
                          : 'bg-fsgu-green-100 text-fsgu-green-700'
                        : isDarkMode
                        ? 'bg-fsgu-gray-700 text-fsgu-silver-400'
                        : 'bg-fsgu-silver-200 text-fsgu-silver-600'
                    }`}>
                      {club.active ? '✓ Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className={`p-3 rounded-lg ${
                    isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                  }`}>
                    <Building2 className={`w-6 h-6 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                  </div>
                </div>

                {/* Club Details */}
                <div className="space-y-3 mb-6">
                  {club.city && (
                    <div className="flex items-start space-x-3">
                      <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {club.address ? `${club.address}, ` : ''}{club.city}
                      </span>
                    </div>
                  )}

                  {club.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className={`w-4 h-4 flex-shrink-0 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                      }`} />
                      <a 
                        href={`tel:${club.phone}`}
                        className={`text-sm hover:underline ${
                          isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                        }`}
                      >
                        {club.phone}
                      </a>
                    </div>
                  )}

                  {club.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className={`w-4 h-4 flex-shrink-0 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                      }`} />
                      <a 
                        href={`mailto:${club.email}`}
                        className={`text-sm hover:underline truncate ${
                          isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                        }`}
                      >
                        {club.email}
                      </a>
                    </div>
                  )}

                  {club.website && (
                    <div className="flex items-center space-x-3">
                      <Globe className={`w-4 h-4 flex-shrink-0 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                      }`} />
                      <a 
                        href={club.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-sm hover:underline truncate ${
                          isDarkMode ? 'text-fsgu-blue-400' : 'text-fsgu-blue-600'
                        }`}
                      >
                        Visit Website →
                      </a>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className={`flex gap-2 pt-4 border-t ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}>
                  <Link
                    to={`/admin/clubs/edit/${club.id}`}
                    className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-fsgu-gray-700 hover:bg-fsgu-gray-600 text-fsgu-silver-200'
                        : 'bg-fsgu-silver-100 hover:bg-fsgu-silver-200 text-fsgu-silver-700'
                    }`}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(club.id, club.name)}
                    className={`p-2.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-fsgu-red-900/20 hover:bg-fsgu-red-900/30 text-fsgu-red-400'
                        : 'bg-fsgu-red-50 hover:bg-fsgu-red-100 text-fsgu-red-600'
                    }`}
                    title="Delete club"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminClubs;